/**
 * Run non-critical work (third-party widgets, analytics SDKs) only once the
 * page has loaded AND the main thread is idle, so it never competes with the
 * first paint, the LCP resources or hydration on a slow phone (M6).
 *
 * `minDelayMs` additionally waits until that many ms after navigation start
 * (e.g. a support-chat launcher nobody needs in the first seconds).
 * Returns a cancel function (call it from an effect cleanup).
 */
export function afterLoadAndIdle(
  callback: () => void,
  options: { timeout?: number; minDelayMs?: number } = {},
): () => void {
  const { timeout = 3000, minDelayMs = 0 } = options
  let cancelled = false
  let idleHandle: number | null = null
  let timer: ReturnType<typeof setTimeout> | null = null

  const run = () => {
    if (!cancelled) callback()
  }
  const idle = () => {
    if (cancelled) return
    const wait = Math.max(0, minDelayMs - performance.now())
    timer = setTimeout(() => {
      // Safari < 18 has no requestIdleCallback.
      if ('requestIdleCallback' in window) {
        idleHandle = window.requestIdleCallback(run, { timeout })
      } else {
        timer = setTimeout(run, 1200)
      }
    }, wait)
  }

  if (document.readyState === 'complete') idle()
  else window.addEventListener('load', idle, { once: true })

  return () => {
    cancelled = true
    window.removeEventListener('load', idle)
    if (timer !== null) clearTimeout(timer)
    if (idleHandle !== null && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(idleHandle)
    }
  }
}
